import { Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { cloneDeep, invoke, omit } from '@sama/micro-dash';
import { toolNameToShapeType } from '../../../../../cross-team-shared/annotation/shared/abstract-tool';
import { deleteShapes } from '../../../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import { ShapeLocationState2D } from '../../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { ShapeState2D } from '../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { AbstractToolHelper } from '../../../../../cross-team-shared/annotation/shared/tool-helpers/abstract-tool-helper';
import { selectionToArray } from '../../../../../cross-team-shared/misc-utils';
import { addUngroupedShape } from '../../../../../cross-team-shared/state/shape.state';
import { VectorTaggingToolObjectless } from '../../../../../cross-team-shared/tools-names';
import { ShapeGroupOrderState } from '../../../../../team-apis/content/content-connector';
import { EventTrackingValue } from '../../../../../team-apis/core/event-tracking';
import { IN_PROGRESS_INDEX } from '../../../../../team-apis/core/reserved-shape-indexes';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../../../team-apis/core/shell-connector';
import { AbstractSensorContentState2D } from '../../../state/abstract-sensor-content-state-2d';
import { AbstractSensorContentStore2D } from '../../../state/abstract-sensor-content-store-2d';

export interface ShapeTool {
  name: VectorTaggingToolObjectless;

  buildFirstKeyLocation?(
    initialValues: ShapeLocationState2D,
  ): ShapeLocationState2D;
  buildShape(initialValues: ShapeState2D): ShapeState2D;
  prepareKeyLocationForCommit?(location: ShapeLocationState2D): void;
  prepareShapeForCommit?(shape: ShapeState2D): void;
  notifyDrawFinished(shape: ShapeState2D | undefined): void;
}

/**
 * In general a shape tool helper handles all the behavior
 * between the first click of a shape and completing or canceling it,
 * and notifying the tool at that time.
 */
export abstract class AbstractShapeToolHelper<
  T extends ShapeTool = ShapeTool,
> extends AbstractToolHelper {
  inProgressShapeStore: Store<ShapeState2D>;

  protected shellConnector: ShellConnector;
  protected store: AbstractSensorContentStore2D;
  protected shapeIndex: number;

  constructor(
    protected tool: T,
    private overlayStore: Store<ShapeGroupOrderState>,
    injector: Injector,
  ) {
    super();

    this.shellConnector = injector.get(shellConnectorToken);
    this.store = injector.get(AbstractSensorContentStore2D);
    this.shapeIndex = this.shellConnector.claimNextInternalId();
    this.inProgressShapeStore = overlayStore('shapes')(IN_PROGRESS_INDEX);
  }

  startListening() {
    this.store.batch(() => {
      const shape = this.tool.buildShape({
        type: toolNameToShapeType(this.tool.name),
        index: IN_PROGRESS_INDEX,
        tags: {},
        dynamic_tags: {},
        key_locations: [this.buildKeyLocation(this.store.state())],
      });
      addUngroupedShape(shape, this.overlayStore);
      this.shellConnector.modifyShapeSelection([IN_PROGRESS_INDEX]);
    });
  }

  override cancel(eventTrackingValue: EventTrackingValue) {
    super.cancel(eventTrackingValue);
    this.tool.notifyDrawFinished(undefined);

    // Shape tools set the selection to index 0, the in progress shape. When
    // cancelling the tool, we need to clear that in-progress selection.
    this.shellConnector.modifyShapeSelection(
      selectionToArray(omit(this.store.state().selection, IN_PROGRESS_INDEX)),
    );
  }

  protected override complete() {
    const shape = cloneDeep(this.inProgressShapeStore.state());
    shape.index = this.shapeIndex;
    invoke(this.tool, ['prepareKeyLocationForCommit'], shape.key_locations[0]);
    invoke(this.tool, ['prepareShapeForCommit'], shape);
    this.tool.notifyDrawFinished(shape);
  }

  protected override cleanUp() {
    deleteShapes([IN_PROGRESS_INDEX], this.overlayStore);
    super.cleanUp();
  }

  private buildKeyLocation(state: AbstractSensorContentState2D) {
    let keyLocation: ShapeLocationState2D = {
      frame_number: state.frameNumber,
      points: [],
      visibility: 1,
    };
    if (this.tool.buildFirstKeyLocation) {
      keyLocation = this.tool.buildFirstKeyLocation(keyLocation);
    }
    return keyLocation;
  }
}
